/**
 * Aba "Anotações da Lu" da ficha da cliente, idêntica à do legado (`admin.html:2747-2756`,
 * `5419-5445`; linha 208 do inventário, e a trava de botão da linha 179).
 *
 * Um texto livre por cliente e lead, com "Última atualização em ..." e o salvar com
 * "Salvando...", "Salvo!" (some em 3 s) ou "Erro ao salvar: " com a mensagem crua.
 *
 * Território da frente A3. A moldura importa esta aba pelo nome `AbaAnotacoesLu`.
 */

// Estado e memória de função do React.
import { useCallback, useState } from 'react';
// As peças do Chassi.
import { Botao, CampoTexto } from '@complexity/ui';
// A leitura e a gravação das anotações.
import { lerAnotacoesLu, salvarAnotacoesLu } from '@studiolu/dados/areas/ficha-anotacoes-lu';
// A fonte de dados do painel.
import { usePainel } from '../../../contexto.js';
// O status que some sozinho, peça comum.
import { LinhaDeStatus, useStatusTemporario } from '../../../comum/status-temporario.js';
// O contrato da aba.
import type { FichaAberta, PropsDaAba } from '../comum-ficha/tipos.js';
import { useCarregadorDaAba } from '../comum-ficha/carregador.js';
// "Última atualização em ...", como o `toLocaleString('pt-BR')` do legado.
import { textoUltimaAtualizacao } from '../comum-ficha/regras.js';
// As peças da A3.
import { useEscopoEstavel } from './comum-a3/escopo.js';
import { useRecortePorPapel } from './comum-a3/papel.js';
import { AbaSemFicha } from './comum-a3/sem-ficha.js';

export function AbaAnotacoesLu({ ficha }: PropsDaAba) {
  // Sem a ficha aberta, a aba não consulta nada (ver `sem-ficha.tsx`).
  if (!ficha) return <AbaSemFicha aba="Anotações da Lu" />;
  return <ConteudoAnotacoes ficha={ficha} />;
}

function ConteudoAnotacoes({ ficha }: { readonly ficha: FichaAberta }) {
  const { dados } = usePainel();
  const { somenteLeitura } = useRecortePorPapel();
  const escopo = useEscopoEstavel(ficha);
  const [conteudo, setConteudo] = useState('');
  // O "Salvar" nasce travado e só destrava depois da leitura (linha 179, `admin.html:5978` e
  // `5431`): salvar antes gravaria o texto que estivesse no campo, de outra cliente.
  const [carregado, setCarregado] = useState(false);
  const [status, mostrar, fixar] = useStatusTemporario();

  // A leitura da aba, como o `carregarAnotacoesLu` do legado (`admin.html:5419-5432`). Ela limpa o
  // status e reescreve o campo, e é por isso que o rascunho desta aba NÃO sobrevive à troca de aba:
  // quem apaga é o próprio carregador, como no legado.
  const carregar = useCallback(async () => {
    setCarregado(false);
    fixar('');
    const resultado = await lerAnotacoesLu(dados, escopo);
    // DEFEITO DO LEGADO REPRODUZIDO (achado da A3, fora da seção 4): falha na leitura só vai para
    // o console, o campo fica vazio e o "Salvar" destrava, então salvar gravaria o vazio por cima
    // do texto que não carregou (`admin.html:5426-5431`).
    if (!resultado.ok) console.error('Erro ao carregar anotações da Lu:', resultado.erro.mensagem);
    const linha = resultado.ok ? resultado.dados : null;
    setConteudo(linha?.conteudo || '');
    if (linha?.atualizado_em) fixar(textoUltimaAtualizacao(linha.atualizado_em));
    setCarregado(true);
  }, [dados, escopo, fixar]);

  // A carga da aba, pelo contrato: a moldura a roda antes de mostrar a aba e a cada toque no botão.
  useCarregadorDaAba(carregar);

  // "Salvar anotações" (`admin.html:5434-5445`).
  const salvar = async () => {
    fixar('Salvando...');
    // `atualizado_em` é o instante do clique, como o `new Date().toISOString()` do legado.
    const resultado = await salvarAnotacoesLu(dados, escopo, conteudo, new Date().toISOString());
    // Este ponto do legado mostra a mensagem CRUA depois do prefixo, e não a tradução.
    if (!resultado.ok) {
      fixar('Erro ao salvar: ' + resultado.erro.mensagem);
      return;
    }
    mostrar('Salvo!');
  };

  return (
    <section className="flex flex-col gap-cx-3">
      <h3 className="text-cx-base font-semibold text-texto">Anotações da Lu</h3>
      <p className="text-cx-sm text-texto-suave">
        Espaço livre para anotações diversas da Lu sobre essa cliente: observações, lembretes ou qualquer
        informação que não se encaixe nos outros campos do atendimento.
      </p>
      {/* Consulta: campo desabilitado (`admin.html:3394`). */}
      <CampoTexto
        rotulo="Anotações"
        linhas={10}
        placeholder="Escreva aqui suas anotações sobre essa cliente..."
        value={conteudo}
        disabled={somenteLeitura}
        onChange={(evento) => setConteudo(evento.target.value)}
      />
      {/* Consulta não vê botão de ação (`admin.html:3395`). */}
      {!somenteLeitura && (
        <div>
          <Botao disabled={!carregado} onClick={() => void salvar()}>
            Salvar anotações
          </Botao>
        </div>
      )}
      <LinhaDeStatus texto={status} testid="status-anotacoes-lu" />
    </section>
  );
}
